import { useEffect, useMemo, useState } from "react";
import { format, startOfMonth, subMonths } from "date-fns";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Link2,
  Copy,
  Check,
  Trash2,
  Loader2,
  FileDown,
  ExternalLink,
} from "lucide-react";

import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

// `report_snapshots` is added by a migration and is not in the generated
// Database types yet; use an untyped view of the client for those calls.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sdb = supabase as unknown as { from: (table: string) => any };

import type { DisplayCurrency } from "@/hooks/finance/cash-flow/useCashFlow";
import { useCashFlowStatementDetails } from "@/hooks/finance/cash-flow/useCashFlow";
import { useProfitAndLoss } from "@/hooks/finance/pnl/useProfitAndLoss";
import { useBalanceSheet } from "@/hooks/finance/balance-sheet/useBalanceSheet";

import { FinancialReportTable } from "@/components/finance/reports/financial-report/FinancialReportTable";
import { ProfitAndLossTable } from "@/components/finance/reports/pnl-report/ProfitAndLossTable";
import { BalanceSheetTable } from "@/components/finance/reports/balance-sheet/BalanceSheetTable";
import { exportCombinedInvestorPdf } from "@/components/finance/reports/shared/exportCombinedInvestorPdf";
import { exportCombinedInvestorExcel } from "@/components/finance/reports/shared/exportCombinedInvestorExcel";
import {
  serializeCashFlowRows,
  serializeBSRows,
  serializeMetaMap,
  type SerializedReportBundle,
  type SnapshotPayload,
} from "@/components/finance/reports/shared/snapshotSerialize";

// Both the cash-flow and balance-sheet hooks accept "monthly" and "quarterly".
type InvestorViewPeriod = "monthly" | "quarterly";

// How long the date inputs must be idle before the (expensive) six-report load
// starts. Long enough to spin/type through a date without triggering a fetch.
const DATE_SETTLE_MS = 700;

type SnapshotRow = {
  id: string;
  created_at: string;
  title: string | null;
  date_from: string | null;
  date_to: string | null;
  currencies: string[] | null;
  revoked: boolean;
};

export default function InvestorSnapshotGeneratorPage() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const today = format(new Date(), "yyyy-MM-dd");
  // Default to the past 3 months (this month + the 2 before it), e.g. in July
  // that's May 1 → today, giving three monthly columns.
  const threeMonthsAgo = format(startOfMonth(subMonths(new Date(), 2)), "yyyy-MM-dd");
  // What the date inputs show. A native <input type="date"> fires onChange on
  // every intermediate value (each month you spin past, each partial edit), so
  // these are drafts only — they must not drive the queries directly.
  const [dateFrom, setDateFrom] = useState(threeMonthsAgo);
  const [dateTo, setDateTo] = useState(today);

  // What actually drives the six report queries. Settled from the drafts only
  // once the user stops changing the date, so picking a date doesn't kick off
  // (and abandon) a full 6-report load on every keystroke/spin.
  const [appliedFrom, setAppliedFrom] = useState(threeMonthsAgo);
  const [appliedTo, setAppliedTo] = useState(today);

  const datesPending = dateFrom !== appliedFrom || dateTo !== appliedTo;

  useEffect(() => {
    // Ignore incomplete (cleared) or inverted ranges mid-edit — never query them.
    if (!dateFrom || !dateTo || dateFrom > dateTo) return;
    if (dateFrom === appliedFrom && dateTo === appliedTo) return;
    const t = setTimeout(() => {
      setAppliedFrom(dateFrom);
      setAppliedTo(dateTo);
    }, DATE_SETTLE_MS);
    return () => clearTimeout(t);
  }, [dateFrom, dateTo, appliedFrom, appliedTo]);

  const [previewCurrency, setPreviewCurrency] = useState<DisplayCurrency>("ETB");
  const [viewPeriod, setViewPeriod] = useState<InvestorViewPeriod>("monthly");

  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 3 reports × 2 currencies — all data gathered here so the snapshot captures
  // both ETB and USD in one payload.
  const cfEtb = useCashFlowStatementDetails(appliedFrom, appliedTo, viewPeriod, "ETB");
  const cfUsd = useCashFlowStatementDetails(appliedFrom, appliedTo, viewPeriod, "USD");
  const pnlEtb = useProfitAndLoss(appliedFrom, appliedTo, viewPeriod, "ETB");
  const pnlUsd = useProfitAndLoss(appliedFrom, appliedTo, viewPeriod, "USD");
  const bsEtb = useBalanceSheet(appliedFrom, appliedTo, viewPeriod, "ETB");
  const bsUsd = useBalanceSheet(appliedFrom, appliedTo, viewPeriod, "USD");

  const buildBundle = (
    cf: typeof cfEtb,
    pnl: typeof pnlEtb,
    bs: typeof bsEtb
  ): SerializedReportBundle | null => {
    if (cf.isLoading || pnl.isLoading || bs.isLoading) return null;
    return {
      cashflow: {
        rows: serializeCashFlowRows(cf.rows),
        periodLabels: cf.periodLabels,
        periodKeys: cf.periodKeys,
        openingMeta: serializeMetaMap(cf.openingMetaByPeriod),
        closingMeta: serializeMetaMap(cf.closingMetaByPeriod),
      },
      pnl: {
        rows: serializeCashFlowRows(pnl.rows),
        periodLabels: pnl.periodLabels,
        periodKeys: pnl.periodKeys,
      },
      balanceSheet: {
        rows: serializeBSRows(bs.rows),
        periods: bs.periods,
      },
    };
  };

  const etbBundle = useMemo(
    () => buildBundle(cfEtb, pnlEtb, bsEtb),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cfEtb.rows, cfEtb.isLoading, pnlEtb.rows, pnlEtb.isLoading, bsEtb.rows, bsEtb.isLoading]
  );
  const usdBundle = useMemo(
    () => buildBundle(cfUsd, pnlUsd, bsUsd),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cfUsd.rows, cfUsd.isLoading, pnlUsd.rows, pnlUsd.isLoading, bsUsd.rows, bsUsd.isLoading]
  );

  // Ready only when both currency bundles are loaded AND the date inputs have
  // settled — otherwise the data on screen doesn't match the dates shown.
  const allReady = !!etbBundle && !!usdBundle && !datesPending;

  // Live hook outputs for the currently previewed currency.
  const cf = previewCurrency === "ETB" ? cfEtb : cfUsd;
  const pnl = previewCurrency === "ETB" ? pnlEtb : pnlUsd;
  const bs = previewCurrency === "ETB" ? bsEtb : bsUsd;

  const snapshots = useQuery({
    queryKey: ["report-snapshots", user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data, error } = await sdb
        .from("report_snapshots")
        .select("id, created_at, title, date_from, date_to, currencies, revoked")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SnapshotRow[];
    },
  });

  const publicUrl = (id: string) => `${window.location.origin}/investor/${id}`;

  const handleGenerate = async () => {
    if (!etbBundle || !usdBundle) return;
    setGenerating(true);
    setError(null);
    setGeneratedLink(null);
    try {
      // Always stamp the snapshot with the dates the captured data actually
      // reflects (the settled ones) — never an in-flight draft.
      const payload: SnapshotPayload = {
        version: 1,
        company: "ChipChip",
        dateFrom: appliedFrom,
        dateTo: appliedTo,
        viewPeriod,
        generatedAt: new Date().toISOString(),
        currencies: ["ETB", "USD"],
        reports: { ETB: etbBundle, USD: usdBundle },
      };
      const { data, error } = await sdb
        .from("report_snapshots")
        .insert({
          title: `ChipChip Financials — ${appliedFrom} to ${appliedTo}`,
          date_from: appliedFrom,
          date_to: appliedTo,
          currencies: ["ETB", "USD"],
          payload,
        })
        .select("id")
        .single();
      if (error) throw error;
      setGeneratedLink(publicUrl(data.id));
      queryClient.invalidateQueries({ queryKey: ["report-snapshots", user?.id] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to generate link");
    } finally {
      setGenerating(false);
    }
  };

  const copy = async (text: string) => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const revoke = async (id: string) => {
    await sdb.from("report_snapshots").update({ revoked: true }).eq("id", id);
    queryClient.invalidateQueries({ queryKey: ["report-snapshots", user?.id] });
  };

  // Both currencies, built once and shared by the combined PDF and Excel.
  const bothCurrencyBundles = () => ({
    etb: {
      cashflow: { rows: cfEtb.rows, periodLabels: cfEtb.periodLabels, periodKeys: cfEtb.periodKeys },
      pnl: { rows: pnlEtb.rows, periodLabels: pnlEtb.periodLabels, periodKeys: pnlEtb.periodKeys },
      balanceSheet: { rows: bsEtb.rows, periods: bsEtb.periods },
    },
    usd: {
      cashflow: { rows: cfUsd.rows, periodLabels: cfUsd.periodLabels, periodKeys: cfUsd.periodKeys },
      pnl: { rows: pnlUsd.rows, periodLabels: pnlUsd.periodLabels, periodKeys: pnlUsd.periodKeys },
      balanceSheet: { rows: bsUsd.rows, periods: bsUsd.periods },
    },
  });

  const downloadCombinedPdf = () => {
    exportCombinedInvestorPdf({
      ...bothCurrencyBundles(),
      fileName: `chipchip-financials-ETB-USD-${format(new Date(), "yyyyMMdd")}.pdf`,
    });
  };

  const downloadCombinedExcel = () => {
    exportCombinedInvestorExcel({
      ...bothCurrencyBundles(),
      fileName: `chipchip-financials-ETB-USD-${format(new Date(), "yyyyMMdd")}.xlsx`,
    });
  };

  return (
    <Layout>
      <div className="space-y-6 pb-12">
        {/* Controls */}
        <Card className="border-none shadow-lg bg-white">
          <CardHeader className="bg-slate-900 border-none pb-4">
            <CardTitle className="text-white text-base flex items-center gap-2">
              <Link2 className="h-4 w-4" /> Investor Snapshot
            </CardTitle>
            <CardDescription className="text-slate-400 text-[11px]">
              Generate one read-only public link to Cashflow, P&amp;L and Balance Sheet
              (both ETB &amp; USD). Investors can view and download; figures are frozen
              at generation time and never change.
            </CardDescription>
          </CardHeader>
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-wrap items-end gap-4">
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600">
                From
                <input
                  type="date"
                  value={dateFrom}
                  max={dateTo}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-8 rounded-md border border-slate-200 px-2 text-xs"
                />
              </label>
              <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600">
                To
                <input
                  type="date"
                  value={dateTo}
                  min={dateFrom}
                  max={today}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-8 rounded-md border border-slate-200 px-2 text-xs"
                />
              </label>

              <label className="flex flex-col gap-1 text-[11px] font-semibold text-slate-600">
                Period
                <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5 h-8">
                  {(["monthly", "quarterly"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setViewPeriod(p)}
                      className={
                        "px-3 rounded text-[10px] font-bold capitalize transition-all " +
                        (viewPeriod === p
                          ? "bg-slate-900 text-white"
                          : "text-slate-500 hover:text-slate-900")
                      }
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </label>

              <div className="flex items-center gap-1 rounded-md border border-slate-200 p-0.5">
                {(["ETB", "USD"] as const).map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setPreviewCurrency(c)}
                    className={
                      "px-3 py-1 rounded text-[10px] font-bold transition-all " +
                      (previewCurrency === c
                        ? "bg-slate-900 text-white"
                        : "text-slate-500 hover:text-slate-900")
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>

              <Button
                onClick={handleGenerate}
                disabled={!allReady || generating}
                className="h-8 text-xs gap-1.5"
              >
                {generating ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Link2 className="h-3.5 w-3.5" />
                )}
                {allReady ? "Generate investor link" : "Loading data…"}
              </Button>

              <Button
                variant="secondary"
                onClick={downloadCombinedPdf}
                disabled={!allReady}
                className="h-8 text-xs gap-1.5"
                title="Download all three reports, both currencies, as one PDF"
              >
                <FileDown className="h-3.5 w-3.5" /> Combined PDF (ETB + USD)
              </Button>

              <Button
                variant="secondary"
                onClick={downloadCombinedExcel}
                disabled={!allReady}
                className="h-8 text-xs gap-1.5"
                title="Download all three reports for both currencies as one Excel workbook"
              >
                <FileDown className="h-3.5 w-3.5" /> Combined Excel (ETB + USD)
              </Button>
            </div>

            {error && (
              <div className="rounded-md bg-rose-50 border border-rose-200 px-3 py-2 text-xs text-rose-700">
                {error}
              </div>
            )}

            {generatedLink && (
              <div className="rounded-md bg-emerald-50 border border-emerald-200 px-3 py-2 flex items-center gap-2">
                <span className="text-xs font-mono text-emerald-900 truncate flex-1">
                  {generatedLink}
                </span>
                <Button
                  size="sm"
                  variant="secondary"
                  className="h-7 text-[11px] gap-1"
                  onClick={() => copy(generatedLink)}
                >
                  {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copied ? "Copied" : "Copy"}
                </Button>
                <a href={generatedLink} target="_blank" rel="noopener noreferrer">
                  <Button size="sm" variant="secondary" className="h-7 text-[11px] gap-1">
                    <ExternalLink className="h-3 w-3" /> Open
                  </Button>
                </a>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Management list */}
        <Card className="border-none shadow-lg bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Active investor links</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-widest">
                    <th className="px-4 py-2 text-left font-black">Created</th>
                    <th className="px-4 py-2 text-left font-black">Range</th>
                    <th className="px-4 py-2 text-left font-black">Status</th>
                    <th className="px-4 py-2 text-right font-black">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {snapshots.data?.length ? (
                    snapshots.data.map((s) => (
                      <tr key={s.id} className={s.revoked ? "opacity-50" : ""}>
                        <td className="px-4 py-2">
                          {format(new Date(s.created_at), "MMM d, yyyy HH:mm")}
                        </td>
                        <td className="px-4 py-2 text-slate-600">
                          {s.date_from} → {s.date_to}
                        </td>
                        <td className="px-4 py-2">
                          {s.revoked ? (
                            <Badge variant="secondary" className="text-[9px]">
                              Revoked
                            </Badge>
                          ) : (
                            <Badge className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20 text-[9px]">
                              Active
                            </Badge>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-[11px] gap-1"
                              onClick={() => copy(publicUrl(s.id))}
                              disabled={s.revoked}
                            >
                              <Copy className="h-3 w-3" /> Copy
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-7 text-[11px] gap-1 text-rose-600 hover:text-rose-700"
                              onClick={() => revoke(s.id)}
                              disabled={s.revoked}
                            >
                              <Trash2 className="h-3 w-3" /> Revoke
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="px-4 py-6 text-center text-slate-400 text-xs">
                        {snapshots.isLoading ? "Loading…" : "No links generated yet."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Live preview of what investors will see (previewed currency) */}
        <div className="space-y-6">
          <FinancialReportTable
            rows={cf.rows}
            periodLabels={cf.periodLabels}
            periodKeys={cf.periodKeys}
            isLoading={cf.isLoading}
            displayCurrency={previewCurrency}
            openingMetaByPeriod={cf.openingMetaByPeriod}
            closingMetaByPeriod={cf.closingMetaByPeriod}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
          <ProfitAndLossTable
            rows={pnl.rows}
            periodLabels={pnl.periodLabels}
            periodKeys={pnl.periodKeys}
            isLoading={pnl.isLoading}
            displayCurrency={previewCurrency}
          />
          <BalanceSheetTable
            rows={bs.rows}
            periods={bs.periods}
            isLoading={bs.isLoading}
            displayCurrency={previewCurrency}
          />
        </div>
      </div>
    </Layout>
  );
}
